// Lightweight API helper for CaratJewel Expo.
// All calls use REACT_APP_BACKEND_URL and the `/api` prefix.

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function request(path, options = {}) {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const error = new Error(`API ${res.status}: ${text || res.statusText}`);
    try {
      const detail = JSON.parse(text).detail;
      if (detail?.code === "package_limit") error.message = detail.message;
    } catch { /* Preserve existing error handling for other endpoints. */ }
    error.status = res.status;
    throw error;
  }
  if (res.status === 204) return null;
  return res.json();
}

export const api = {
  site: (path, body, method="PATCH") => request(path, body === undefined ? {} : {method, body:JSON.stringify(body)}),
  communication: (path, body, options = {}) => request(path, body === undefined ? {cache: "no-store", ...options} : {method: "POST", body: JSON.stringify(body), ...options}),
  listExhibitors: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/exhibitors${qs ? `?${qs}` : ""}`);
  },
  getExhibitor: (id, options = {}) => request(`/exhibitors/${encodeURIComponent(id)}`, options),
  updateExhibitorStatus: (id, status) =>
    request(`/exhibitors/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),
  listProducts: (params = {}, options = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/products${query ? `?${query}` : ""}`, options);
  },
  getProduct: (id, exhibitorId, options = {}) => request(
    `/products/${encodeURIComponent(id)}${exhibitorId ? `?exhibitor_id=${encodeURIComponent(exhibitorId)}` : ""}`, options),
  myProducts: () => request("/me/products"),
  createProduct: (payload) =>
    request("/products", { method: "POST", body: JSON.stringify(payload) }),
  updateProduct: (id, payload) =>
    request(`/products/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  deleteProduct: (id) => request(`/products/${id}`, { method: "DELETE" }),
  listExhibitions: () => request("/exhibitions", {cache: "no-store"}),
  updateLiveSchedule: (id, payload) => request(`/exhibitions/${encodeURIComponent(id)}/live-schedule`, {method: "PATCH", body: JSON.stringify(payload)}),
  getExhibition: (id) => request(`/exhibitions/${id}`),

  createInquiry: (payload) =>
    request("/inquiries", { method: "POST", body: JSON.stringify(payload) }),
  listInquiries: () => request("/inquiries"),
  myInquiries: () => request("/me/inquiries"),
  updateInquiry: (id, payload) =>
    request(`/inquiries/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  createMeeting: (payload) =>
    request("/meetings", { method: "POST", body: JSON.stringify(payload) }),
  listMeetings: () => request("/meetings"),
  myMeetings: () => request("/me/meetings"),
  updateMeeting: (id, payload) =>
    request(`/meetings/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  rescheduleMeeting: (id, payload) =>
    request(`/meetings/${id}/reschedule`, { method: "POST", body: JSON.stringify(payload) }),
  createApplication: (payload) =>
    request("/exhibitor-applications", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  uploadApplicationProof: (id, token, file) =>
    request(`/exhibitor-applications/${encodeURIComponent(id)}/proof`, {
      method: "POST",
      headers: {
        "Content-Type": file.type,
        "X-Application-Upload-Token": token,
        "X-File-Name": encodeURIComponent(file.name),
      },
      body: file,
    }),
  applicationProofUrl: (id) => `${BASE}/exhibitor-applications/${encodeURIComponent(id)}/proof`,
  listApplications: (status) =>
    request(`/exhibitor-applications${status ? `?status=${status}` : ""}`),
  myExhibitorApplications: () => request("/me/exhibitor-applications", {cache: "no-store"}),
  updateApplicationStatus: (id, status, details = {}) =>
    request(`/exhibitor-applications/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify({ ...details, status }),
    }),
  registerBuyer: (payload) =>
    request("/buyer-registrations", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  listBuyerRegistrations: () => request("/buyer-registrations"),

  addShortlist: (payload) =>
    request("/me/shortlist", { method: "POST", body: JSON.stringify(payload) }),
  listShortlist: (kind) =>
    request(`/me/shortlist${kind ? `?kind=${kind}` : ""}`),
  removeShortlist: (kind, itemId) =>
    request(`/me/shortlist/${kind}/${encodeURIComponent(itemId)}`, {
      method: "DELETE",
    }),

  adminSummary: () => request("/admin/summary"),
  listUsers: () => request("/admin/users"),
  updateUserRole: (userId, payload) =>
    request(`/admin/users/${userId}/role`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),

  // Verified Buyer
  myVerification: () => request("/me/buyer-verification"),
  submitVerification: (payload) =>
    request("/me/buyer-verification", { method: "POST", body: JSON.stringify(payload) }),
  listVerifications: (status) =>
    request(`/admin/buyer-verifications${status ? `?status=${status}` : ""}`),
  decideVerification: (id, payload) =>
    request(`/admin/buyer-verifications/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  verifiedBuyerEmails: () => request("/me/verified-buyer-emails"),

  // Agora video
  agoraToken: (payload) =>
    request("/agora/token", { method: "POST", body: JSON.stringify(payload) }),
  startBroadcast: (payload) => request("/me/broadcast/start", { method: "POST", body: JSON.stringify(payload) }),
  publishBroadcast: (payload) => request("/me/broadcast/published", { method: "POST", body: JSON.stringify(payload) }),
  heartbeatBroadcast: (payload) => request("/me/broadcast/heartbeat", { method: "POST", body: JSON.stringify(payload) }),
  endBroadcast: (payload) => request("/me/broadcast/end", { method: "POST", body: JSON.stringify(payload), keepalive: true }),
  broadcastStatus: (id) => request(`/broadcasts/${encodeURIComponent(id)}`, { cache: "no-store" }),
  buyerMeetings: () => request("/me/buyer-meetings"),
  getMeeting: (id) => request(`/meetings/${encodeURIComponent(id)}`, {cache: "no-store"}),
  meetingEvents: () => request("/me/meeting-events", {cache: "no-store"}),
  claimMeetingEvent: (id) => request(`/me/meeting-events/${encodeURIComponent(id)}/claim`, {method: "POST"}),

  // Phase 8B commercial layer (provider-neutral; Razorpay test checkout only)
  myCommercial: () => request("/commercial/me", {cache: "no-store"}),
  commercialOrder: (id) => request(`/commercial/orders/${encodeURIComponent(id)}`, {cache: "no-store"}),
  createStandardOrder: (applicationId) => request("/commercial/orders/standard", {
    method: "POST", body: JSON.stringify({application_id: applicationId}),
  }),
  requestCustomPackage: (payload = {}) => request("/commercial/custom-requests", {
    method: "POST", body: JSON.stringify(payload),
  }),
  acceptCommercialOffer: (id, revision) => request(`/commercial/offers/${encodeURIComponent(id)}/accept`, {
    method: "POST", body: JSON.stringify({offer_revision: revision}),
  }),
  createCheckout: (id, idempotencyKey) => request(`/commercial/orders/${encodeURIComponent(id)}/checkout`, {
    method: "POST", body: JSON.stringify({idempotency_key: idempotencyKey}),
  }),
  verifyRazorpayCheckout: (payload) => request("/commercial/payments/razorpay/verify", {
    method: "POST", body: JSON.stringify(payload),
  }),
  adminCommercial: () => request("/admin/commercial", {cache: "no-store"}),
  createCommercialOffer: (payload) => request("/admin/commercial/offers", {
    method: "POST", body: JSON.stringify(payload),
  }),
  editCommercialOffer: (id, payload) => request(`/admin/commercial/offers/${encodeURIComponent(id)}`, {
    method: "PATCH", body: JSON.stringify(payload),
  }),
  sendCommercialOffer: (id, rowVersion) => request(`/admin/commercial/offers/${encodeURIComponent(id)}/send`, {
    method: "POST", body: JSON.stringify({row_version: rowVersion}),
  }),
  reviseCommercialOffer: (id, rowVersion) => request(`/admin/commercial/offers/${encodeURIComponent(id)}/revise`, {
    method: "POST", body: JSON.stringify({row_version: rowVersion}),
  }),
  cancelCommercialOffer: (id, rowVersion) => request(`/admin/commercial/offers/${encodeURIComponent(id)}/cancel`, {
    method: "POST", body: JSON.stringify({row_version: rowVersion}),
  }),
  grantCommercialWaiver: (payload) => request("/admin/commercial/waivers", {
    method: "POST", body: JSON.stringify(payload),
  }),
  requestCommercialRefund: (payload) => request("/admin/commercial/refunds", {
    method: "POST", body: JSON.stringify(payload),
  }),
  submitCommercialRefund: (id) => request(`/admin/commercial/refunds/${encodeURIComponent(id)}/submit`, {method: "POST"}),
  classifyLegacyCommercial: (id, payload) => request(`/admin/commercial/legacy/${encodeURIComponent(id)}`, {
    method: "PATCH", body: JSON.stringify(payload),
  }),
  updateTaxConfiguration: (payload) => request("/admin/commercial/tax-configuration", {
    method: "PATCH", body: JSON.stringify(payload),
  }),
};
